import { VisuItemLabel } from '../../common/models/label.model';

export type CalendarRecurrence = 'Daily' | 'Weekdays' | 'Weekends' | 'CustomDays';

export interface CalendarEvent {
  uid: string;
  title: string;
  start: Date;
  end: Date;
  position?: {
    top?: number;
    height?: number;
    left?: number;
    width?: number;
    column?: number;
    overlap?: number;
    /**
     * Distance in pixels from the block's top to its `cutoff`, or null when the
     * cutoff is absent or falls outside the span visible on this day.
     */
    cutoffOffset?: number | null;
    /**
     * True when this block is the tail of a window that started on an earlier
     * day: its top edge is a cut at midnight, not the event's real start.
     */
    continuesBefore?: boolean;
    /** True when the window runs past this day's midnight. */
    continuesAfter?: boolean;
  };
  description?: string;
  /** Hex color value (e.g., '#ff0000') */
  mainColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  recurrence?: {
    type: CalendarRecurrence;
    customDays?: number[]; // 0 = Sunday, 1 = Monday, etc. (for CustomDays type)
    endDate?: Date; // When recurrence should stop
    interval?: number; // Every X days/weeks (default: 1)
    /** Occurrence dates (YYYY-MM-DD) removed from the series (exdates). */
    excludedDates?: string[];
    /**
     * Inclusive lower bound of the validity window: occurrences strictly before
     * this instant are not rendered. Maps from the schedule's `valid_from`.
     */
    validFrom?: Date;
    /**
     * Inclusive upper bound of the validity window: occurrences strictly after
     * this instant are not rendered. Maps from the schedule's `valid_to`.
     */
    validTo?: Date;
  };
  editable?: boolean; // Whether the event can be moved/resized by the user
  /**
   * Deadline within [start, end] after which arriving counts as late. Rendered
   * inside the block as the boundary of its late region, not as its own event.
   */
  cutoff?: Date | null;
  labels: VisuItemLabel[];
}

/**
 * Emitted when a calendar event is moved or resized by the user.
 * `start`/`end` are the new times after snapping. Persisted by the data-visu
 * container for non-recurring events and the `recurring` whole-series scope.
 */
export interface CalendarEventChange {
  uid: string;
  start: Date;
  end: Date;
}

/**
 * Edit scope for a recurring event:
 * - `exception`: create a single one-off exception for the dragged occurrence.
 * - `recurring`: apply the change recursively to the whole recurring series.
 * - `next`: split the series at the dragged occurrence — this and all following
 *   occurrences take the new times, earlier ones keep the original schedule.
 */
export type RecurrenceEditScope = 'exception' | 'recurring' | 'next';

/**
 * Public payload emitted by the calendar after a move/resize. Carries the
 * recurrence edit scope chosen by the user; `scope` is undefined for a
 * non-recurring event. Intended for the backend (GBE) wiring.
 */
export interface CalendarEventChangeWithScope extends CalendarEventChange {
  scope?: RecurrenceEditScope;
}

/**
 * Describes how to persist a move/resize of a calendar event back to its
 * source entity. Built per event uid by `buildCalendarModifications`; each
 * entity encapsulates its own time-rewrite semantics in `withNewTimes`.
 */
export interface CalendarEventModification {
  /** Base uid; the map key, matching the uid produced by `buildCalendarEvents`. */
  uid: string;
  /** GBE API entity-type path segment (e.g. 'job_visit', 'site'). */
  entityType: string;
  /** Transformer entity-type version (e.g. 'UserJobVisit', 'UserSite'). */
  entityTypeVersion: string;
  /**
   * The entity type whose table shows the event, when it is not the one the PATCH goes to
   * (a site schedule saved through its site). Its views are told to refetch too.
   */
  refreshEntityType?: string;
  /**
   * Partial PATCH body for the times update: `{ uid, ...changedFields }` only
   * (post-#1205 convention), NOT the full raw row.
   */
  withNewTimes(start: Date, end: Date): Record<string, unknown>;
  /**
   * Single-occurrence exception: returns a partial PATCH that excepts
   * `occurrenceDate` from the recurring schedule and appends a one-time
   * schedule at the new times. Only implemented by entities with recurring
   * schedules (UserSite); absent ⇒ the exception scope is unsupported.
   */
  withException?(occurrenceDate: string, start: Date, end: Date): Record<string, unknown>;
  /**
   * "This and following": returns a partial PATCH that splits the recurring
   * schedule at `occurrenceDate`. The original schedule is bounded with
   * `valid_to` = the day before `occurrenceDate`, and a copy of it (same
   * recurrence/config) is appended with `valid_from` = `occurrenceDate` and the
   * new times, so this and every later occurrence adopt the change. Only
   * implemented by entities with recurring schedules (UserSite); absent ⇒ the
   * `next` scope is unsupported.
   */
  withFollowing?(occurrenceDate: string, start: Date, end: Date): Record<string, unknown>;
}

export enum PossibleViews {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month'
}

/** One event pill in a month cell, with its colours already resolved. */
export interface MonthMarker {
  event: CalendarEvent;
  /** The schedule's own hue, worn by the pill's left rail. */
  color: string;
  /** Tinted pill background. */
  fill: string;
  /** Ink for the cutoff chip, legible on `fill`. */
  ink: string;
  /** Tooltip body: the full time range, plus the cutoff when there is one. */
  tooltip: string;
}

export interface MonthDay {
  day: Date;
  isToday: boolean;
  /** A leading or trailing cell borrowed from the adjacent month. */
  isOutsideMonth: boolean;
  isWeekend: boolean;
  visibleMarkers: MonthMarker[];
  /** Events the cell had no room for, surfaced as `+n`. */
  hiddenMarkerCount: number;
}

/** One column of the day or week grid. */
export interface TimeGridDay {
  id: number;
  day: Date;
  isToday: boolean;
  isWeekend: boolean;
  /** Weekday name for the column header, already localised. */
  shortLabel: string;
  markers: CalendarEvent[];
}

export interface Hour {
  hour: string;
  /** Position of the hairline, in pixels from the top of the column. */
  top: number;
  /** Position of the label, nudged so it straddles its own hairline. */
  labelTop: number;
}
